import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../../components/modal/Modal';
import { getCoachDeletionPreviewAPI } from '../../api/employee';
import ModalCoachDeleteMembers from './ModalCoachDeleteMembers';
import { CarryApiResult, CoachDeletionPreview } from '../../types/coachCarry';
import { useAccessMode } from '../../context/AccessModeContext';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    onClickDeleteDirect: () => void;
    onClickDeleteWithReassign: () => void;
    centerId?: string;
    coachId?: string;
    accessToken?: string;
    onCarry?: () => void;
    onDeletePerMember?: (convertMemberIds: string[]) => Promise<CarryApiResult<unknown>>;
    onRefreshTrainerList?: () => Promise<void>;
};

type Method = 'carry' | 'handoff' | 'members' | 'convert' | 'direct';
const methods: { value: Method; title: string; situation: string; description: string; detail: string }[] = [
    { value: 'carry', title: '회원과 함께 다른 센터로 이동', situation: '라포를 쓰는 다른 센터로 이직할 때',
        description: '남은 수강권을 옮겨 갈 센터에서 이어서 써요. 지난 기록과 매출은 이 센터에 남아요.',
        detail: '먼저 받는 센터와 이동할 회원을 선택해 주세요. 이어받기 결과를 확인한 뒤 강사를 삭제해 주세요.' },
    { value: 'handoff', title: '다른 강사에게 넘기고 삭제', situation: '퇴사, 회원은 센터의 다른 강사가 담당',
        description: '사용 중인 수강권과 수업일지를 다른 강사에게 넘겨요.',
        detail: '회원 리스트에서 담당강사를 먼저 변경해 주세요. 인계가 끝나면 다시 강사 삭제를 진행해 주세요.' },
    { value: 'members', title: '회원마다 정하고 삭제', situation: '데려갈 회원과 남길 회원이 섞여 있을 때',
        description: '센터에 남길 회원을 한 명씩 골라요.',
        detail: '센터 회원으로 전환할 회원만 선택해 주세요. 다른 센터로 이동하거나 다른 강사에게 넘길 회원은 이어받기·인계를 먼저 마쳐 주세요.' },
    { value: 'convert', title: '센터 회원으로 바꾸고 삭제', situation: '퇴사, 회원은 센터에 남김',
        description: '회원은 센터에 남고 강사만 빠져요. 사용 중인 PT 수강권은 만료돼요.',
        detail: '사용 중인 PT 수강권은 만료되고 예정 수업은 취소돼요. 그룹 수강권은 유지돼요. 다른 강사 소속 회원은 이 강사와의 연결만 해제돼요.' },
    { value: 'direct', title: '바로 삭제', situation: '회원과 함께 개인 강사로 이어서 이용',
        description: '강사가 회원을 데리고 개인 강사로 라포를 계속 써요. 수강권은 이 센터에 남아요.',
        detail: '나중에 강사가 다른 라포 센터에 합류하면 회원도 그 센터로 따라가요. 다른 라포 센터로 옮기는 경우라면 「회원과 함께 다른 센터로 이동」을 먼저 진행해 주세요. 그래야 남은 수강권이 옮겨 갈 센터에서 열려요.' },
];

const ModalCoachDelete = ({ setIsVisible, onClickDeleteDirect, onClickDeleteWithReassign,
    centerId, coachId, accessToken, onCarry, onDeletePerMember, onRefreshTrainerList }: Props) => {
    const { isReadOnly } = useAccessMode();
    const [step, setStep] = useState<'method' | 'handoff' | 'members' | 'confirm'>('method');
    const [method, setMethod] = useState<Method | null>(null);
    const [checked, setChecked] = useState(false);
    const [preview, setPreview] = useState<CoachDeletionPreview | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [previewError, setPreviewError] = useState(false);
    const [busy, setBusy] = useState(false);
    const submitting = useRef(false);
    const resultUnknown = useRef(false);
    const previewRequest = useRef(0);
    const close = () => { if (!submitting.current && !resultUnknown.current) setIsVisible(false); };
    const activePTCount = preview ? new Set(preview.withActivePT.map((member) => member.memberId)).size : 0;

    const fetchPreview = useCallback(async () => {
        const request = ++previewRequest.current;
        setIsLoadingPreview(true);
        setPreviewError(false);
        setPreview(null);
        setChecked(false);
        try {
            if (!centerId || !coachId || !accessToken) throw new Error('삭제 영향 조회 정보 없음');
            const data = await getCoachDeletionPreviewAPI(centerId, coachId, accessToken);
            if (request !== previewRequest.current) return;
            if (Array.isArray(data?.withActivePT) && Array.isArray(data?.withOnlyGroupTickets)
                && Array.isArray(data?.withNoTickets) && Number.isSafeInteger(data.totalMembers) && data.totalMembers >= 0
                && [...data.withActivePT, ...data.withOnlyGroupTickets, ...data.withNoTickets]
                    .every((member) => typeof member?.memberId === 'string' && member.memberId.length > 0)
                && (data.multiCoachSkipped === undefined || (Number.isSafeInteger(data.multiCoachSkipped) && data.multiCoachSkipped >= 0))) {
                setPreview(data);
            } else {
                setPreviewError(true);
            }
        } catch {
            if (request !== previewRequest.current) return;
            setPreviewError(true);
        } finally {
            if (request === previewRequest.current) setIsLoadingPreview(false);
        }
    }, [centerId, coachId, accessToken]);

    useEffect(() => {
        fetchPreview();
        return () => { previewRequest.current += 1; };
    }, [fetchPreview]);

    useEffect(() => { document.getElementById('coach-delete-title')?.focus(); }, [step]);

    const next = () => {
        setChecked(false);
        if (method === 'carry') { if (!isReadOnly) onCarry?.(); }
        else if (method === 'handoff') setStep('handoff');
        else if (method === 'members') setStep('members');
        else if (method === 'direct' || method === 'convert') {
            if (method === 'convert') fetchPreview();
            setStep('confirm');
        }
    };
    const submit = () => {
        if (!checked || submitting.current || isReadOnly || isLoadingPreview || (method === 'convert' && !preview)) return;
        submitting.current = true;
        setBusy(true);
        if (method === 'direct') onClickDeleteDirect();
        else if (method === 'convert') onClickDeleteWithReassign();
    };
    const back = () => { if (resultUnknown.current) return; setChecked(false); setStep('method'); };
    const primaryClass = 'min-h-[44px] rounded-[8px] px-[24px] py-[12px] text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40';

    return (
        <Modal whiteViewWidth={680} isCloseOutside={false} onClose={close}>
            <section role="dialog" aria-modal="true" aria-labelledby="coach-delete-title" className="flex max-h-[calc(100vh-48px)] flex-col p-[32px] text-[14px] leading-[22px] text-LAVEL_4">
                {step === 'members' && centerId && coachId && accessToken && onDeletePerMember ? (
                    <ModalCoachDeleteMembers centerId={centerId} coachId={coachId} accessToken={accessToken}
                        onClose={back} onCarry={onCarry} onDelete={onDeletePerMember}
                        onResultUnknown={() => { resultUnknown.current = true; }} onRefreshTrainerList={onRefreshTrainerList}
                        onBusyChange={(value) => { submitting.current = value; }} />
                ) : <>
                    <p className="mb-[8px] text-[13px] text-LAVEL_2">강사 삭제 · {step === 'method' ? '1단계' : '2단계'}</p>
                    <h2 id="coach-delete-title" tabIndex={-1} className="mb-[16px] text-[22px] font-bold leading-[32px] focus:outline-none">
                        {step === 'method' ? '어떻게 정리할까요?' : step === 'handoff' ? '회원 리스트에서 먼저 넘겨 주세요' : '강사 삭제 전 확인해 주세요'}
                    </h2>
                    <div className="min-h-0 overflow-y-auto">
                        {isLoadingPreview ? <p role="status" className="mb-[20px] text-LAVEL_2">연결된 회원과 수강권을 확인하고 있어요.</p>
                            : preview ? <p className="mb-[20px] text-[13px] text-LAVEL_2">이 강사와 연결된 회원 {preview.totalMembers}명 · 사용 중인 PT가 있는 회원 {activePTCount}명{preview.multiCoachSkipped !== undefined && preview.multiCoachSkipped > 0 ? ` · 다른 강사 소속 ${preview.multiCoachSkipped}명` : ''}</p> : null}
                        {previewError && <p role="alert" className="mb-[16px] text-ERROR">
                            <span className="block font-medium">연결된 회원 정보를 불러오지 못했어요.</span>
                            <span className="mt-[4px] block">다시 확인을 눌러 삭제 영향을 확인해 주세요.</span>
                            <button type="button" onClick={fetchPreview} className="ml-[8px] text-primary underline">다시 확인</button>
                        </p>}
                        {step === 'method' && <div role="radiogroup" aria-label="회원 정리 방법" className="space-y-[8px]">
                            {methods.map((option) => {
                                const disabled = (option.value === 'carry' && !onCarry)
                                    || (option.value === 'members' && (!onDeletePerMember || !centerId || !coachId || !accessToken));
                                return <div key={option.value} className={`relative rounded-[8px] border border-solid p-[16px] ${method === option.value ? 'border-primary bg-BLUE_50' : 'border-GREY_50'} ${disabled ? 'opacity-40' : ''}`}>
                                    <label className="flex cursor-pointer items-start gap-[12px] pr-[56px]">
                                        <input type="radio" name="coach-delete-method" value={option.value} aria-label={option.title}
                                            className="mt-[4px] h-[16px] w-[16px] shrink-0 accent-primary" checked={method === option.value} disabled={disabled}
                                            onChange={() => { setMethod(option.value); setChecked(false); }} />
                                        <span>
                                            <span className="flex items-center gap-[8px] font-bold">{option.title}{option.value === 'direct' && <span className="rounded-full bg-ERROR_50 px-[8px] text-[13px] text-ERROR">주의</span>}</span>
                                            <span className="mt-[2px] block text-[13px] font-medium text-primary">({option.situation})</span>
                                            <span className="mt-[4px] block text-[13px] text-LAVEL_2">{option.description}</span>
                                        </span>
                                    </label>
                                    <details>
                                        <summary aria-label={`${option.title} 자세히`} className="absolute right-[16px] top-[16px] cursor-pointer text-[13px] text-LAVEL_2">자세히</summary>
                                        <p className="mt-[12px] border-t border-solid border-GREY_50 pt-[12px] text-[13px] text-LAVEL_2">{option.detail}</p>
                                    </details>
                                </div>;
                            })}
                        </div>}
                        {step === 'handoff' && <>
                            <p className="mb-[20px] text-LAVEL_2">수강권과 수업일지를 다른 강사에게 인계한 뒤 다시 삭제해 주세요.</p>
                            <ol className="list-decimal space-y-[12px] rounded-[8px] bg-GREY py-[20px] pl-[40px] pr-[20px]">
                                <li>페이지 아래 회원 리스트에서 인계할 회원을 선택해요.</li>
                                <li>‘선택 담당강사 변경’을 눌러 강사를 선택해요.</li>
                                <li>인계 결과를 확인하고 다시 강사 삭제를 진행해요.</li>
                            </ol>
                        </>}
                        {step === 'confirm' && <>
                            <p className="mb-[16px] font-bold">{method === 'convert' ? '회원을 센터에 남기고 강사를 삭제해요.' : '강사와 회원의 센터 연결을 해제해요.'}</p>
                            {method === 'convert' && preview && <div className="mb-[20px] rounded-[8px] bg-ERROR_50 p-[16px]">
                                <p className="font-bold">{activePTCount}명의 PT 수강권이 만료되고 예정 수업이 취소돼요</p>
                                <p className="mt-[4px] text-[13px] text-LAVEL_2">그룹 수강권은 유지돼요.</p>
                            </div>}
                            {method === 'direct' && <p className="mb-[20px] rounded-[8px] bg-ERROR_50 p-[16px]">수강권은 자동으로 이어받지 않아요. 이동할 회원은 이어받기를 먼저 마쳐 주세요.</p>}
                            <details className="mb-[20px] text-[13px] text-LAVEL_2">
                                <summary className="cursor-pointer">삭제 후에는 어떻게 되나요?</summary>
                                <p className="mt-[8px]">강사의 라포 앱에서 센터 연결이 끊겨요. 지난 기록은 강사 관리에서 확인할 수 있고, 다시 연결하면 이어서 작성할 수 있어요.</p>
                                {method === 'convert' && preview && <p className="mt-[8px]">센터 회원으로 전환할 회원 {preview.totalMembers}명 · 그룹권만 있는 회원 {preview.withOnlyGroupTickets.length}명 · 수강권 없는 회원 {preview.withNoTickets.length}명 · {preview.multiCoachSkipped === undefined ? '다른 강사 소속 회원 수는 확인이 필요해요. 해당 회원은' : `다른 강사 소속 ${preview.multiCoachSkipped}명은`} 이 강사와의 연결만 해제돼요.</p>}
                            </details>
                            <label className="flex items-center gap-[8px]">
                                <input type="checkbox" className="h-[16px] w-[16px] accent-primary" checked={checked} disabled={busy || isLoadingPreview || isReadOnly || (method === 'convert' && !preview)}
                                    onChange={(event) => setChecked(event.target.checked)} />
                                {method === 'convert' ? '수강권 만료·수업 취소와 강사 삭제를 확인했어요' : '센터 연결 해제와 강사 삭제를 확인했어요'}
                            </label>
                        </>}
                    </div>
                    <div className="mt-[24px] flex shrink-0 justify-end gap-[12px]">
                        <button type="button" disabled={busy} onClick={step === 'method' ? close : back} className="min-h-[44px] rounded-[8px] bg-GREY px-[24px] py-[12px] text-[15px] font-bold disabled:opacity-40">{step === 'method' ? '취소' : '돌아가기'}</button>
                        {step === 'method' ? <button type="button" onClick={next} disabled={!method || isLoadingPreview || isReadOnly} className={`${primaryClass} bg-primary`}>{method === 'carry' ? '이어받기 하기' : '다음'}</button>
                            : step === 'handoff' ? <button type="button" onClick={close} className={`${primaryClass} bg-primary`}>회원 리스트로 돌아가기</button>
                                : <button type="button" onClick={submit} disabled={!checked || busy || isLoadingPreview || (method === 'convert' && !preview) || isReadOnly} className={`${primaryClass} bg-ERROR`}>{busy ? '삭제하고 있어요' : '강사 삭제'}</button>}
                    </div>
                </>}
            </section>
        </Modal>
    );
};

export default ModalCoachDelete;
